import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./styles/App.scss";

import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import {
  ApiClient,
  getLocalStorageValue,
  setLocalStorageValue,
} from "@xompass/web-sdk";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import {
  AuthPayload,
  BootstrapPayload,
  normalizeUserType,
  persistAuthPayload,
  waitForBootstrapFromParent,
} from "./utils/iframeUtils";

dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

const spinner = document.createElement("div");
spinner.className = "app-spinner-backdrop";
spinner.innerHTML = `<div class="app-spinner"><div class="app-spinner__bg"></div><div class="app-spinner__logo"></div></div>`;
spinner.id = "entry-spinner";
document.body.appendChild(spinner);

const AUTH_TIMEOUT_MS = 7000;
const AUTH_VALIDATION_TTL_MS = 10 * 60 * 1000;
const AUTH_VALIDATION_KEY = "vsaas$authValidatedAt";
type AuthValidationState = {
  token?: string;
  userId?: string;
  validatedAt?: number;
};

function getAuthFromUrl(): AuthPayload | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("tk");
  const userId = params.get("uid");
  const userType = normalizeUserType(params.get("ut"));

  if (!token || !userId || !userType) {
    return null;
  }

  const varId = params.get("varId") || undefined;
  return { token, userId, userType, varId };
}

function getStoredAuth(): AuthPayload | null {
  const token = getLocalStorageValue("vsaas$accessToken");
  const userId = getLocalStorageValue("vsaas$userId");
  const userTypeRaw = getLocalStorageValue("vsaas$principalType");
  const userType = normalizeUserType(userTypeRaw || null);

  if (!token || !userId || !userType) {
    return null;
  }

  const varId = getLocalStorageValue("vsaas$varId") || undefined;
  return { token, userId, userType, varId };
}

function clearAuthStorage() {
  setLocalStorageValue("vsaas$accessToken", null);
  setLocalStorageValue("vsaas$userId", null);
  setLocalStorageValue("vsaas$principalType", null);
  setLocalStorageValue("vsaas$varId", null);
  setLocalStorageValue(AUTH_VALIDATION_KEY, null);
}

function shouldSkipValidation(payload: AuthPayload): boolean {
  const lastValidated = getLocalStorageValue(AUTH_VALIDATION_KEY);
  if (!lastValidated || typeof lastValidated !== "object") {
    return false;
  }

  const { token, userId, validatedAt } = lastValidated as AuthValidationState;
  if (
    token !== payload.token ||
    userId !== payload.userId ||
    typeof validatedAt !== "number"
  ) {
    return false;
  }

  return Date.now() - validatedAt < AUTH_VALIDATION_TTL_MS;
}

function markAuthValidated(payload: AuthPayload) {
  setLocalStorageValue(AUTH_VALIDATION_KEY, {
    token: payload.token,
    userId: payload.userId,
    validatedAt: Date.now(),
  });
}

function isSameAuthPayload(a: AuthPayload, b: AuthPayload): boolean {
  return (
    a.token === b.token &&
    a.userId === b.userId &&
    a.userType === b.userType &&
    a.varId === b.varId
  );
}

function normalizeBootstrapAuth(
  payload: BootstrapPayload | null,
): AuthPayload | null {
  return payload?.auth || null;
}

async function validateAuth(payload: AuthPayload): Promise<boolean> {
  const baseUrl = import.meta.env.VITE_API_PATH as string | undefined;
  if (!baseUrl) {
    return false;
  }

  const apiClient = new ApiClient(baseUrl);
  apiClient.setAccessToken(payload.token);
  apiClient.setUserId(payload.userId);
  apiClient.setPrincipalType(payload.userType);

  try {
    await apiClient.login();
    return true;
  } catch {
    return false;
  }
}

async function bootstrapAuth(): Promise<void> {
  const hostBootstrapPromise = waitForBootstrapFromParent(AUTH_TIMEOUT_MS);
  let payload: AuthPayload | null = getStoredAuth();
  const urlAuth = getAuthFromUrl();
  if (urlAuth) {
    persistAuthPayload(urlAuth);
    payload = urlAuth;
  }

  if (!payload) {
    payload = normalizeBootstrapAuth(await hostBootstrapPromise);
  }

  if (payload) {
    if (shouldSkipValidation(payload)) {
      return;
    }

    const validated = await validateAuth(payload);
    if (validated) {
      markAuthValidated(payload);
      return;
    }

    const hostPayload = normalizeBootstrapAuth(await hostBootstrapPromise);
    if (hostPayload && !isSameAuthPayload(hostPayload, payload)) {
      payload = hostPayload;
      const hostValidated = await validateAuth(payload);
      if (hostValidated) {
        markAuthValidated(payload);
        return;
      }
    }

    const retryValidated = await validateAuth(payload);
    if (!retryValidated) {
      clearAuthStorage();
      return;
    }

    markAuthValidated(payload);
  }
}

bootstrapAuth()
  .catch(() => null)
  .finally(() => {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    );
  });
